import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { useOnboarding } from "./useOnboarding";
import { STEPS, LAST_EDIT_STEP } from "./onboardingModel";
import { OnboardingAssistant } from "./OnboardingAssistant";
import { StepIdentify } from "./StepIdentify";
import { StepConfirm } from "./StepConfirm";
import { StepCustomers } from "./StepCustomers";
import { StepTargeting } from "./StepTargeting";
import { StepPreview } from "./StepPreview";
import { StepCommit } from "./StepCommit";
import { StepLaunch } from "./StepLaunch";

const LOGO = "https://customer-assets.emergentagent.com/job_pro-advisors-4/artifacts/00joaz6p_017767_Logo%20FInal%20File-01.png";

const Stepper = ({ step }) => (
  <ol className="flex items-center gap-2 flex-wrap" data-testid="onboarding-stepper">
    {STEPS.map((s, i) => (
      <li key={s.id} className="flex items-center gap-2">
        <span className={`h-1.5 w-8 rounded-full transition-colors ${i < step ? "bg-ism-teal/60" : i === step ? "bg-ism-teal" : "bg-white/10"}`} />
        <span className={`text-[11px] hidden sm:inline ${i === step ? "text-white" : "text-gray-600"}`}>{s.title}</span>
      </li>
    ))}
  </ol>
);

export const OnboardingFlow = ({ userId, instanceId, instanceName, onComplete, onClose, closable = false }) => {
  const ob = useOnboarding({ userId, instanceId, instanceName });
  const go = (n) => ob.setStep(n);

  const analyzeThenNext = async () => { if (await ob.analyze()) go(1); };
  const previewThenNext = async () => { go(4); await ob.runPreview(); };
  const launch = async () => { const res = await ob.complete(); if (res) go(6); };

  const body = () => {
    if (!ob.ready) return <div className="py-20 text-center"><Loader2 className="h-8 w-8 animate-spin text-ism-teal mx-auto" /></div>;
    switch (ob.step) {
      case 0: return <StepIdentify data={ob.data} patch={ob.patch} job={ob.job} busy={ob.busy} onNext={analyzeThenNext} />;
      case 1: return <StepConfirm data={ob.data} patch={ob.patch} patchSpec={ob.patchSpec} busy={ob.busy} onBack={() => go(0)} onNext={() => go(2)} onReanalyze={analyzeThenNext} />;
      case 2: return <StepCustomers userId={userId} instanceId={instanceId} data={ob.data} patch={ob.patch} patchSpec={ob.patchSpec} busy={ob.busy} onBack={() => go(1)} onNext={() => go(3)} />;
      case 3: return <StepTargeting data={ob.data} patch={ob.patch} patchSpec={ob.patchSpec} busy={ob.busy} onBack={() => go(2)} onNext={previewThenNext} />;
      case 4: return <StepPreview data={ob.data} job={ob.job} busy={ob.busy} onBack={() => go(3)} onNext={() => go(5)} onRetry={() => ob.runPreview()} onRetryUnblocked={() => ob.runPreview({ skipPreflight: true })} onApplyGuidance={ob.applyGuidance} />;
      case 5: return <StepCommit data={ob.data} patch={ob.patch} busy={ob.busy} onBack={() => go(4)} onLaunch={launch} />;
      default: return <StepLaunch userId={userId} huntJobId={ob.data.huntJobId} onFinish={() => onComplete?.(userId)} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0a0e17]/95 backdrop-blur-md overflow-y-auto" data-testid="onboarding-flow">
      <div className="min-h-full flex items-start justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-6xl">
          <div className="flex items-center justify-between mb-8">
            <img src={LOGO} alt="Intelligent Sales Machines" className="h-12 w-auto" />
            <div className="flex items-center gap-4">
              <Stepper step={ob.step} />
              {closable && <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="onboarding-close-btn"><X className="h-5 w-5" /></button>}
            </div>
          </div>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
            <div>
              <AnimatePresence mode="wait">
                <motion.div key={ob.step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}
                  className="rounded-2xl border border-white/10 bg-[#0f1419]/80 p-6 sm:p-8 shadow-2xl">
                  {body()}
                </motion.div>
              </AnimatePresence>
              <p className="text-center text-[11px] text-gray-600 mt-6">Your progress is saved automatically — you can leave and pick up where you left off.</p>
            </div>
            {ob.ready && ob.step <= LAST_EDIT_STEP && <OnboardingAssistant userId={userId} step={ob.step} data={ob.data} patch={ob.patch} patchSpec={ob.patchSpec} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
